import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";
import useLocales from "src/locales/use-locales";
import { palette } from "../../figma-palette";
import { SplitBarCellData } from "../split-bar-cell";
import SplitBarCell from "../split-bar-cell";

interface GridDataTooltipProps {
    data: SplitBarCellData[];
    total: number;
    format: (value: number) => string;
    onMouseLeave?: () => void;
    title: string;
}

export default function GridDataTooltip({
    data,
    total,
    format,
    onMouseLeave = () => { },
    title,
}: GridDataTooltipProps) {
    const theme = useTheme();
    const { t } = useLocales();

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: `1px solid rgba(0, 0, 0, 0.12)`,
                borderRadius: '12px',
                overflow: 'hidden',
                minWidth: 250,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Box
                sx={{
                    backgroundColor: alpha("#919EAB", 0.08),
                    px: 2,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: 1,
                        color: "#212B36",
                        flexGrow: 1,
                    }}
                >
                    {t(title)}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: 1,
                        color: "#637381",
                    }}
                >{format(total)}</Typography>
            </Box>

            {/* Bar Chart */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                }}
            >
                <SplitBarCell sx={{ height: "8px", my: "4px"}} data={data} total={0} format={format} />
            </Box>

            {/* Legend */}
            <Box
                sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                    px: 2,
                    py: 2,
                }}
            >
                <Stack spacing={1.5}>
                    {data.map((item) => (
                        <Stack key={item.name} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 20,
                                        backgroundColor: item.color,
                                        borderRadius: '16px',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: 1,
                                        color: "#637381",
                                    }}
                                >
                                    {t(item.name)}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: "#212B36",
                                    textAlign: 'right',
                                }}
                            >
                                {item.format?.(item.value) || format(item.value)}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

